
import React from 'react';
import { BookOpen, FileText, Headphones, Film, Pencil, Trash2, Link as LinkIcon, ChevronUp, ChevronDown } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CivicEducationItem } from '@/types/civic-education';
import { useIsMobile } from '@/hooks/use-mobile';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';

interface CivicEducationTableProps {
  items: CivicEducationItem[];
  isLoading: boolean;
  contentType: string;
  onEdit: (item: CivicEducationItem) => void;
  onDelete: (id: string) => void;
  onManageRelationships: (id: string) => void;
  sortField?: keyof CivicEducationItem;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: keyof CivicEducationItem) => void;
}

const CivicEducationTable: React.FC<CivicEducationTableProps> = ({
  items,
  isLoading,
  contentType,
  onEdit,
  onDelete,
  onManageRelationships,
  sortField = 'created_at',
  sortDirection = 'desc',
  onSort
}) => {
  // Filter items based on content type is now handled by pagination hook
  const isMobile = useIsMobile();

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'podcast':
        return <Headphones size={16} className="text-purple-500" />;
      case 'video':
        return <Film size={16} className="text-blue-500" />;
      case 'summary':
        return <FileText size={16} className="text-green-500" />;
      default:
        return <BookOpen size={16} />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-500">Published</Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      case 'archived':
        return <Badge variant="secondary">Archived</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getSortIcon = (field: keyof CivicEducationItem) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />;
  };

  const renderSortableHeader = (field: keyof CivicEducationItem, label: string) => {
    return (
      <div 
        className="flex items-center cursor-pointer" 
        onClick={() => onSort && onSort(field)}
      >
        {label}
        {getSortIcon(field)}
      </div>
    );
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-8 border rounded-md">
        <BookOpen className="mx-auto h-12 w-12 text-gray-300 mb-2" />
        <h3 className="text-lg font-medium">No content found</h3>
        <p className="text-sm text-gray-500 mb-4">
          {contentType === 'all'
            ? "You haven't created any educational content yet."
            : `You haven't created any ${contentType} content yet.`}
        </p>
      </div>
    );
  }

  // Mobile view - card layout
  if (isMobile) {
    return (
      <div className="space-y-4">
        {items.map((item) => (
          <Card key={item.id}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center">
                  {getTypeIcon(item.type)}
                  <CardTitle className="ml-2 text-base">{item.title}</CardTitle>
                </div>
                {getStatusBadge(item.status)}
              </div>
            </CardHeader>
            <CardContent className="pb-3 pt-2">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Status:</span>
                  <span>{getStatusBadge(item.status)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Country:</span>
                  <span>{item.country_code || "Global"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Related Cards:</span>
                  <span>{item.related_issues.length}</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {item.tags.slice(0, 3).map((tag, i) => (
                    <Badge variant="outline" key={i}>{tag}</Badge>
                  ))}
                  {item.tags.length > 3 && (
                    <Badge variant="outline">+{item.tags.length - 3}</Badge>
                  )}
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between pt-2 pb-3 bg-gray-50 dark:bg-gray-800/50">
              <Button variant="outline" size="sm" onClick={() => onEdit(item)}>
                <Pencil size={14} className="mr-1" /> Edit
              </Button>
              <Button variant="outline" size="sm" onClick={() => onManageRelationships(item.id)}>
                <LinkIcon size={14} className="mr-1" /> Link
              </Button>
              <Button variant="destructive" size="sm" onClick={() => onDelete(item.id)}>
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  }

  // Desktop view - table layout
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Type</TableHead>
          <TableHead onClick={() => onSort && onSort('title')}>
            {renderSortableHeader('title', 'Title')}
          </TableHead>
          <TableHead>Tags</TableHead>
          <TableHead onClick={() => onSort && onSort('status')}>
            {renderSortableHeader('status', 'Status')}
          </TableHead>
          <TableHead onClick={() => onSort && onSort('country_code')}>
            {renderSortableHeader('country_code', 'Country')}
          </TableHead>
          <TableHead>Related Cards</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.id}>
            <TableCell className="w-10">{getTypeIcon(item.type)}</TableCell>
            <TableCell className="font-medium">{item.title}</TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {item.tags.slice(0, 2).map((tag, i) => (
                  <Badge variant="outline" key={i}>{tag}</Badge>
                ))}
                {item.tags.length > 2 && (
                  <Badge variant="outline">+{item.tags.length - 2}</Badge>
                )}
              </div>
            </TableCell>
            <TableCell>{getStatusBadge(item.status)}</TableCell>
            <TableCell>{item.country_code || "Global"}</TableCell>
            <TableCell>{item.related_issues.length}</TableCell>
            <TableCell className="text-right space-x-1">
              <Button size="sm" variant="outline" onClick={() => onEdit(item)}>
                <Pencil size={14} className="mr-1" />
                Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => onManageRelationships(item.id)}>
                <LinkIcon size={14} className="mr-1" />
                Link
              </Button>
              <Button size="sm" variant="destructive" onClick={() => onDelete(item.id)}>
                <Trash2 size={14} />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default CivicEducationTable;
